import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import RentalResume from './assets/rentalResume';
import {
  useRentalDetailRelayQuery,
  useRentalUnlocksRelayQuery,
  mapRelayRentalDetailToIRental,
} from '../../hooks/rentals/query/V2';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { ISystem } from '../../ts/interface/system.interface';
import { IMode } from '../../ts/interface/mode.interface';
import { changeTab, findAccess } from '../../utils';
import TransactionInformation from './assets/transactionInformation';
import { IAdmin, IAuth } from '../../ts/interface/admins.interface';
import { IRentalDetailProps } from '../../ts/interface/pages/rentals.interface';
import PageContainer from '../../components/Redesing/page-container';
import Grid from '@mui/material/Unstable_Grid2';
import Stack from '@mui/material/Stack';
import useRentalRoute from '../../hooks/rentals/query/useRentalRoute';
import { IRentalRoute } from '../../ts/interface/rental.interface';
import RentalMap from './assets/rentalMap';
import Card from '@mui/material/Card';
import Badge from '@mui/material/Badge';
import RentalSupport from './assets/rental-support';
import PageContent from '../../components/Redesing/page-content';
import LockClockIcon from '@mui/icons-material/LockClock';
import RentalHardwareEventLogs from './assets/rental-hardware-event-logs';
import getStationCoords from './assets/getStationCoords';
import RentalImageGallery from '../../components/RentalImageGallery/RentalImageGallery';
import useOrganization from '../../hooks/use-organization';
import { useSystemRelay } from '../../hooks/system/query/V2/useSystemRelay';
import { getRentalLocationChecks, getRentalEndData } from '../../api/rentals';
import RentalEndData from './assets/rental-end-data';
import RentalMixpanelEvents from './assets/RentalMixpanelEvents';
import RentalUnlocks from './assets/rental-unlocks';
import { buildRentalUnlockGroups } from './assets/rental-unlock-groups';

interface AccessPermissions {
  [key: string]: (value: boolean) => void;
}

const RentalDetail = ({
  system,
  auth,
  modeInfo,
  systemAccess,
  admins,
  systemLoaded,
}: IRentalDetailProps) => {
  const organizationInfo = useOrganization();
  const isOrgMode = Boolean(organizationInfo?.id);

  const { rentalId, currentTab: currentActiveTab = 'details' } = useParams<{
    rentalId: string;
    currentTab: string;
  }>();
  const [rentalAdminPermission, setRentalAdminPermission] = useState(false);
  const [refundAdminPermission, setRefundAdminPermission] = useState(false);
  const [movaticAccessPermission, setMovaticAccessPermission] = useState(false);
  const [pathLoaded, setPathLoaded] = useState<boolean>(false);
  const [loadedRoutes, setLoadedRoutes] = useState<IRentalRoute[] | null>(null);
  const [currentTab, setCurrentTab] = useState<string>('details');

  useEffect(() => {
    if (currentActiveTab !== currentTab) {
      setCurrentTab(currentActiveTab);
    }
  }, [currentActiveTab, currentTab]);

  const handleTabsChange = useCallback(
    (event: ChangeEvent<any>, value: string): void => {
      changeTab(`${rentalId}/${value}`, currentTab, 'rentals');
    },
    [currentTab, rentalId]
  );

  const {
    data: rentalData,
    isLoading,
    refetch: refetchRental,
  } = useRentalDetailRelayQuery(rentalId, {
    enabled: isOrgMode ? true : systemLoaded,
    useOldId: false, // rentalId from URL is the GraphQL UUID
  });

  // Map GraphQL data to legacy IRental format
  const rental = useMemo(() => {
    if (!rentalData) return {};
    return mapRelayRentalDetailToIRental(rentalData);
  }, [rentalData]);

  const supportTicketCount = rental?.support_messages?.length || 0;

  // Unlock events are fetched separately (and eagerly) so the Unlocks tab
  // badge is ready before the tab is visited, and so a backend failure on
  // the unlocks calculation cannot blank the whole rental payload.
  const {
    data: unlocksData,
    isLoading: isLoadingUnlocks,
    error: unlocksError,
  } = useRentalUnlocksRelayQuery(rentalId, {
    enabled: isOrgMode ? true : systemLoaded,
  });

  const { groups: unlockGroups, failedCount: failedUnlockCount } = useMemo(
    () => buildRentalUnlockGroups(unlocksData),
    [unlocksData]
  );

  const tabs = useMemo(() => {
    return [
      { label: 'Details', value: 'details' },
      {
        label:
          supportTicketCount > 0 ? (
            <Badge
              badgeContent={supportTicketCount}
              color="error"
              sx={{ mr: supportTicketCount > 99 ? 2 : 1.5 }}
            >
              Support
            </Badge>
          ) : (
            'Support'
          ),
        value: 'support',
      },
      { label: 'Event Logs', value: 'hardware-logs' },
      {
        label:
          failedUnlockCount > 0 ? (
            <Badge
              badgeContent={failedUnlockCount}
              color="error"
              sx={{ mr: failedUnlockCount > 99 ? 2 : 1.5 }}
            >
              Unlocks
            </Badge>
          ) : (
            'Unlocks'
          ),
        value: 'unlocks',
      },
      ...(movaticAccessPermission ? [{ label: 'Analytics', value: 'analytics' }] : []),
    ];
  }, [movaticAccessPermission, supportTicketCount, failedUnlockCount]);

  // In org mode, derive modeInfo for the rental's own system via GraphQL
  // (keyed off the rental's system, not the active Redux system).
  const { modeInfo: orgModeInfo } = useSystemRelay(rental?.system?.id ?? undefined, {
    enabled: isOrgMode && Boolean(rental?.system?.id),
  });

  // Resolve modeInfo and system: GraphQL-derived in org mode, Redux in system mode
  const resolvedModeInfo = isOrgMode ? orgModeInfo : modeInfo;
  const resolvedSystem = isOrgMode ? rental?.system || ({} as ISystem) : system;
  const resolvedSystemAccess = isOrgMode ? organizationInfo?.access || 0 : systemAccess;

  const { refetch } = useRentalRoute(
    rentalId,
    {
      enabled: !pathLoaded,
    },
    {
      paginationResultHandler: (res: { data: IRentalRoute[] }) => {
        setLoadedRoutes((sources) => {
          const { data } = res;

          if (!sources) {
            return data;
          }

          const newSources = [...sources];

          data.forEach((item: IRentalRoute) => {
            let index = newSources.findIndex(
              (source: IRentalRoute) => source.id === item.id
            );

            if (index >= 0) {
              newSources[index].route = newSources[index].route.concat(item.route);
            } else {
              newSources.push(item);
            }
          });

          return newSources;
        });
        setPathLoaded(true);
      },
    }
  );

  useEffect(() => {
    refetch().then(() => setPathLoaded(true));
  }, [refetch]);

  const [locationChecks, setLocationChecks] = useState<any[]>([]);
  const [rentalEndData, setRentalEndData] = useState<any>(null);

  useEffect(() => {
    if (rentalId) {
      getRentalLocationChecks(rentalId).then(setLocationChecks);
      getRentalEndData(rentalId).then(setRentalEndData);
    }
  }, [rentalId]);

  const stations = rental?.start ? getStationCoords(rental) : [];

  // Transaction details are fetched via GraphQL by-id hooks (see
  // TransactionInformation), keyed off the ids the rental query already returns.
  const transactionId = rentalData?.charge?.id || rentalData?.chargeId || '';
  const holdTransactionId = rentalData?.ccHold?.id || '';
  const walletTransactionId = rentalData?.walletTransaction?.id || '';

  useEffect(() => {
    if (isOrgMode) {
      setRentalAdminPermission(true);
      setRefundAdminPermission(true);
      setMovaticAccessPermission(true);
      return;
    }
    const accessPermissions: AccessPermissions = {
      rental: setRentalAdminPermission,
      refunds: setRefundAdminPermission,
      movaticAccess: setMovaticAccessPermission,
    };
    const adminAccess = findAccess(systemAccess);
    adminAccess.forEach((eachAccess) => {
      if (accessPermissions[eachAccess]) {
        accessPermissions[eachAccess](true);
      }
    });
  }, [isOrgMode, systemAccess]);

  const showBilling = isOrgMode ? Boolean(rental?.system?.billing) : system.billing;

  return (
    <PageContainer isLoading={isLoading}>
      <PageContent
        dataId="rental-details"
        tabsList={tabs}
        backLink={'/rentals'}
        detailIcon={<LockClockIcon />}
        contentTitle={'Rentals'}
        itemName={`Rental: ${rental?.user?.full_name}`}
        itemChipValue={rental?.id}
        chipLabel={'Rental ID'}
        currentTabV={currentTab}
        handleTabsChange={handleTabsChange}
        tabsChildren={
          <>
            {currentTab === 'details' && (
              <Grid container spacing={2}>
                <Grid xs={12} lg={4}>
                  <Stack spacing={2}>
                    <RentalResume
                      onRentalEnded={() => refetchRental()}
                      rental={rental}
                      system={resolvedSystem}
                      auth={auth}
                      rentalAdminPermission={rentalAdminPermission}
                      modeInfo={resolvedModeInfo}
                      systemAccess={resolvedSystemAccess}
                      systemLoaded={isOrgMode ? true : systemLoaded}
                      showSystem={isOrgMode}
                      movaticAccess={movaticAccessPermission}
                      reviewStatus={rental?.review_status}
                      onReviewResolved={() => refetchRental()}
                    />
                    {(() => {
                      // Combine rental images with end rental photo
                      const allImages = [];

                      // Add end rental photo if it exists
                      if (rental?.end?.image_url) {
                        allImages.push({
                          link: rental.end.image_url,
                          category: 'End Rental Photo',
                          note: null,
                        });
                      }

                      // Add other rental images if they exist
                      if (rental?.images && rental.images.length > 0) {
                        const images = rental.images.map((image: string | object) =>
                          typeof image === 'string' ? JSON.parse(image) : image
                        );
                        allImages.push(...images);
                      }

                      // Only show gallery if there are images
                      if (allImages.length > 0) {
                        return (
                          <RentalImageGallery images={allImages} title="Rental Images" />
                        );
                      }

                      return null;
                    })()}
                    {movaticAccessPermission && rentalEndData && (
                      <RentalEndData data={rentalEndData} />
                    )}
                  </Stack>
                </Grid>
                <Grid xs={12} lg={8}>
                  <Stack spacing={2}>
                    <Card>
                      <Stack padding={2}>
                        {pathLoaded && (
                          <RentalMap
                            systemId={isOrgMode ? rental?.system?.id || '' : system?.gid}
                            coordinatesLoaded={loadedRoutes}
                            hardwareHasGps={rental.hardware?.has_gps}
                            stations={stations || []}
                            systemType={
                              isOrgMode
                                ? rental?.hardware?.type_name
                                : modeInfo.details.hardware_type
                            }
                            systemAccess={resolvedSystemAccess}
                            rentalMarkerType={rental.type}
                            endStationId={
                              rental.end?.station?.id || rental.start?.station?.id
                            }
                            locationChecks={locationChecks}
                          />
                        )}
                      </Stack>
                    </Card>
                    {showBilling && (
                      <TransactionInformation
                        admins={isOrgMode ? [] : admins}
                        walletTransactionId={walletTransactionId}
                        holdTransactionId={holdTransactionId}
                        transactionId={transactionId}
                        movaticAccess={movaticAccessPermission}
                        refundAdminPermission={refundAdminPermission}
                        rental={rental}
                      />
                    )}
                  </Stack>
                </Grid>
              </Grid>
            )}
            {currentTab === 'support' && (
              <RentalSupport supportMessage={rental?.support_messages || []} />
            )}
            {currentTab === 'hardware-logs' && (
              <RentalHardwareEventLogs rental={rental} />
            )}
            {currentTab === 'unlocks' && (
              <RentalUnlocks
                groups={unlockGroups}
                successRate={unlocksData?.unlocksSuccessRate ?? null}
                isLoading={isLoadingUnlocks}
                error={unlocksError}
              />
            )}
            {currentTab === 'analytics' && <RentalMixpanelEvents rentalId={rentalId} />}
          </>
        }
      />
    </PageContainer>
  );
};

const RentalDetailsR = connect(
  (state: {
    system: { current: ISystem; isLoaded: boolean };
    mode: IMode;
    admin: { admin: IAuth; systemAccess: number; systemAdmins: IAdmin[] };
  }) => ({
    auth: state.admin.admin,
    modeInfo: state.mode,
    system: state.system.current,
    systemLoaded: state.system.isLoaded,
    systemAccess: state.admin.systemAccess,
    admins: state.admin.systemAdmins,
  }),
  () => ({})
)(RentalDetail);

export default RentalDetailsR;
